import { Button, ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import TableActionButtons from '@/components/common/TableActionButtons';
import { DATE_SUBMIT_YMD, DEFAULT_SELECT_PAGE_SIZES, DISPPLAY_MOMENT_YMD_HM, ekDomain, ORDER_STATUS_CMS, PAYMENT_STATUS_DATA, ROLE_USER_CONFIG, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ORDER_STATUS, OrderStatusType, ROLE_OWNER, ROLE_USER } from '@/types/enums';
import { ReservationListItem } from '@/types/reservation';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { isRoles } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import classNames from 'classnames';
import { ListFilter, Search } from 'lucide-react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CancelOrder from '../detail/modal/CancelOrder';
import RequestCancel from '../detail/modal/RequestCancel';
import FilterForm from './filter';
import { formatCurrency } from '@/utils/formatNumber';
import Table from '@/components/table/Table';

const DateRangeSchema = yup.object().shape({
    from: yup.date(),
    to: yup.date()
});
// validate schema
const validateSchema = yup.object().shape({
    ownerId: yup.string(),
    orderStatus: yup.string(),
    locationId: yup.string(),
    orderCode: yup.string(),
    rentalFrom: DateRangeSchema.optional().nullable(),
    rentalTo: DateRangeSchema.optional().nullable(),
    customerName: yup.string()
});

type FormData = yup.InferType<typeof validateSchema>;

const acceptCancel = [ROLE_USER_CONFIG[ROLE_USER.SYSTEM_ADMIN].key, ROLE_USER_CONFIG[ROLE_USER.BEST_BIKE_ADMIN].key];

export default observer(function ReservationManagement() {
    // hook
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    const isOwner = isRoles([...ROLE_OWNER]);

    // store
    const {
        reservationStore: {
            paging, setObservable, reservationCMSList, getReservationCMSList,
            totalRecord, clean, keepSearchParams, defaultPaging
        },
        accountStore: {
            getAccounts, paging: accountPaging
        },
        locationStore: {
            getLocations, paging: locationPaging
        },
        authStore: {
            profileEk, profileGas
        },
        modalStore: { showModal, hideAllModals },
        uiStore: { devicesScreen: { desktop } }
    } = useStore();

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            ownerId: keepSearchParams?.ownerId ?? '',
            orderStatus: keepSearchParams?.orderStatus ?? '',
            locationId: keepSearchParams?.locationId ?? '',
            orderCode: keepSearchParams?.orderCode ?? '',
            rentalFrom: keepSearchParams?.rentalFrom ?? null,
            rentalTo: keepSearchParams?.rentalTo ?? null,
            customerName: keepSearchParams?.customerName ?? ''
        }
    });

    // state
    const [rowSelected, setRowSelected] = useState({});

    // lifecycle

    useEffect(() => {
        getAccounts({ isOwner: true }, { ...accountPaging, pagination: { ...accountPaging.pagination, pageIndex: -2 } });
        getLocations({}, { ...locationPaging, pagination: { ...locationPaging.pagination, pageIndex: -2 } });
        onSearch(keepSearchParams);
    }, []);

    // function
    const onSearch = (searchParams?: FormData) => {
        clean();
        const params: any = { ...searchParams };
        if (searchParams?.rentalFrom) {
            params.rentalFromStart = formatDateTime(searchParams.rentalFrom.from, DATE_SUBMIT_YMD);
            params.rentalFromEnd = formatDateTime(searchParams.rentalFrom.to, DATE_SUBMIT_YMD);
        }
        if (searchParams?.rentalTo) {
            params.rentalToStart = formatDateTime(searchParams.rentalTo.from, DATE_SUBMIT_YMD);
            params.rentalToEnd = formatDateTime(searchParams.rentalTo.to, DATE_SUBMIT_YMD);
        }
        onFetch(params, params.paging || defaultPaging);
        searchParams && setObservable('keepSearchParams', toJS(searchParams));
        hideAllModals();
    };

    const onFetch = (searchParams?: any, paging?: TableState) => {
        let convertSearchParams = {
            ownerId: searchParams.ownerId,
            orderStatus: searchParams.orderStatus,
            locationId: searchParams.locationId,
            customerName: searchParams.customerName,
            rentalFromStart: searchParams.rentalFromStart,
            rentalFromEnd: searchParams.rentalFromEnd,
            rentalToStart: searchParams.rentalToStart,
            rentalToEnd: searchParams.rentalToEnd
        };
        getReservationCMSList(convertSearchParams, paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        const params: any = { ...keepSearchParams };
        if (keepSearchParams?.rentalFrom) {
            params.rentalFromStart = formatDateTime(keepSearchParams.rentalFrom.from, DATE_SUBMIT_YMD);
            params.rentalFromEnd = formatDateTime(keepSearchParams.rentalFrom.to, DATE_SUBMIT_YMD);
        }
        if (keepSearchParams?.rentalTo) {
            params.rentalToStart = formatDateTime(keepSearchParams.rentalTo.from, DATE_SUBMIT_YMD);
            params.rentalToEnd = formatDateTime(keepSearchParams.rentalTo.to, DATE_SUBMIT_YMD);
        }

        setObservable('keepSearchParams', toJS({ ...keepSearchParams, paging: newPaging }));
        onFetch(params, newPaging);
    };

    // filter province when change region
    const onClickViewDetail = (id: number) => {
        if (isEk) {
            navigate(`/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`);
        } else {
            navigate(`${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`);
        }
    };

    const onClickRequestCancel = (ids: number[]) => {
        showModal({
            id: 'request-cancel',
            title: t('words_title.request_cancel'),
            size: 'xl',
            titleClassName: 'text-center max-w-none',
            content: (
                <RequestCancel
                    orderIds={ids}
                    reload={() => {
                        setRowSelected({});
                        onSearch(keepSearchParams);
                    }}
                />
            ),
            footerClassName: 'hidden'
        });
    };

    const onClickCancelOrder = (ids: number[]) => {
        showModal({
            id: 'cancel',
            title: t('words_title.cancel_reservation'),
            size: 'xl',
            titleClassName: 'text-center max-w-none',
            content: (
                <CancelOrder
                    orderIds={ids}
                    reload={() => {
                        setRowSelected({});
                        onSearch(keepSearchParams);
                    }}
                />
            ),
            footerClassName: 'hidden'
        });
    };

    const isCancelBtn = useMemo(() => {
        if (isEk) {
            let roleCode = profileEk?.roleCode || '';
            return acceptCancel.includes(roleCode);
        } else {
            let roleCode = profileGas?.roleCode || '';
            return acceptCancel.includes(roleCode);
        }
    }, [pathname]);

    const handleBtnCancel = (ids: number[], isCancel: boolean) => {
        if (isCancel) {
            onClickCancelOrder(ids);
        } else {
            onClickRequestCancel(ids);
        }
    };

    const handleOpenFilter = () => {
        showModal({
            id: 'filter',
            size: 'lg',
            title: t('words_title.filter'),
            titleClassName: 'text-center w-full',
            footerClassName: 'p-0 m-0',
            content: <FilterForm form={form} isInModal={true} onSearch={onSearch} isCancelBtn={isCancelBtn} />
        });
    };

    // columns
    const columns: ColumnDef<ReservationListItem>[] = [
        {
            id: 'actions',
            header: '',
            size: 50,
            cell: ({ row: { original } }) => {
                return (
                    <TableActionButtons
                        onView={() => onClickViewDetail(original.id)}
                    />
                );
            }
        },
        {
            accessorKey: 'rentalDate',
            header: t('field.reservation.rental_date'),
            size: 160,
            enableSorting: false,
            meta: {
                headerClassName: '',
                cellClassName: 'whitespace-nowrap'
            },
            cell: ({ row: { original } }) => (
                <div className=''>{original?.rentalFrom ? formatDateTime(original.rentalFrom, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) : ''}</div>
            )
        },
        {
            accessorKey: 'returnDate',
            header: t('field.reservation.return_date'),
            size: 160,
            enableSorting: false,
            meta: {
                headerClassName: '',
                cellClassName: 'whitespace-nowrap'
            },
            cell: ({ row: { original } }) => (
                <div>{original?.rentalTo ? formatDateTime(original.rentalTo, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) : ''}</div>
            )
        },
        {
            accessorKey: 'location',
            header: t('field.reservation.location_name'),
            size: 200,
            enableSorting: false,
            meta: {
                headerClassName: '',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>{original?.location?.name}</div>
            )
        },
        {
            accessorKey: 'bikeModel',
            header: t('field.model_bike'),
            size: 200,
            enableSorting: false,
            meta: {
                headerClassName: '',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    <Link
                        to={
                            isEk ?
                                `/${ekDomain}${ROUTES.bikeManagement.href}/${ROUTES.bikeEdit.href}/${original?.bikeLicense?.id}` :
                                `${ROUTES.bikeManagement.href}/${ROUTES.bikeEdit.href}/${original?.bikeLicense?.id}`
                        }
                        className='text-[#3B7CEC] hover:underline'
                    >
                        {original?.bikeLicense?.bikeModel}
                    </Link>
                </div>

            )
        },
        {
            accessorKey: 'customerName',
            header: t('field.reservation.customer_name'),
            size: 280,
            enableSorting: false,
            meta: {
                headerClassName: '',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {
                        isOwner ?
                            original.renter?.fullname :
                            (
                                <Link
                                    to={
                                        isEk ?
                                            `/${ekDomain}${ROUTES.userManagement.href}/${ROUTES.userEdit.href}/${original?.renter?.id}` :
                                            `${ROUTES.userManagement.href}/${ROUTES.userEdit.href}/${original?.renter?.id}`
                                    }
                                    className='text-[#3B7CEC] hover:underline'
                                >
                                    {original.renter?.fullname}
                                </Link>
                            )
                    }
                </div>

            )
        },
        {
            accessorKey: 'insurance',
            header: t('field.vehicle_compensation'),
            size: 200,
            meta: {
                cellClassName: '',
                headerClassName: 'center-sub-header-item'
            },
            cell: ({ row: { original } }) => (
                <div className='text-center'>{original?.insurance}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'owner',
            header: t('field.reservation.owner'),
            meta: {
                headerClassName: 'min-w-[90px]',
                cellClassName: ''
            },
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>{original.owner?.fullname}</div>
            )
        },
        {
            accessorKey: 'owner.code',
            header: t('field.user_code'),
            size: 200,
            meta: {
                headerClassName: '',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='w-fit'>{original.owner?.code}</p>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'paymentMethod',
            header: t('field.reservation.payment_method'),
            size: 150,
            meta: {
                headerClassName: 'min-w-[80px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>{original?.payment?.method}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'total',
            // header: t('field.reservation.total_payment'),
            header: '合計金額',
            size: 200,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-right'>
                    {`${t('currency.currency_symbol')}${formatCurrency(original?.payment?.totalPayment || '0')}`}
                </div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'paymentStatus',
            header: t('field.payment_status'),
            size: 100,
            meta: {
                headerClassName: 'center-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className={classNames(`${PAYMENT_STATUS_DATA[original?.payment?.paymentStatus]?.color}`, 'text-center font-medium break-all min-w-[100px]')}>
                    {t(`${PAYMENT_STATUS_DATA[original?.payment?.paymentStatus]?.key}`)}
                </div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'orderStatus',
            header: t('field.reservation.reservation_status'),
            size: 200,
            enableSorting: false,
            meta: {
                headerClassName: '',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className={classNames(`${ORDER_STATUS_CMS[original?.orderStatus]?.color}`, 'font-medium break-all min-w-[100px]')}>
                    {t(`${ORDER_STATUS_CMS[original?.orderStatus]?.key}`)}
                </div>
            )
        },
        {
            id: 'id',
            header: t('field.reservation.reservation_code'),
            accessorKey: 'orderId',
            size: 150,
            meta: {
                headerClassName: '',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div>{original?.orderCodeSystem}</div>
            ),
            enableSorting: false
        }

    ];

    const renderButtonTable = () => {
        return isCancelBtn ?
            (
                <ButtonCusWithIcon
                    variant='default'
                    size='md'
                    className='w-full xsm:w-52 md:h-12 h-9.5'
                    onClick={() => handleBtnCancel(Object.keys(rowSelected).map(key => Number(key)), isCancelBtn)}
                    disabled={Object.keys(rowSelected).length < 1}
                    iconId='#icon-delete'
                >
                    {t('field.reservation.cancel_order')}
                </ButtonCusWithIcon>
            ) :
            (
                <ButtonCus
                    variant='default'
                    size='md'
                    className='w-full xsm:w-52 md:h-12 h-9.5'
                    onClick={() => handleBtnCancel(Object.keys(rowSelected).map(key => Number(key)), isCancelBtn)}
                    disabled={Object.keys(rowSelected).length < 1}
                >
                    {t('field.reservation.request_cancel')}
                </ButtonCus>
            );
    };

    return (
        <>
            {desktop ?
                (
                    <div className='layout-card'>
                        <FilterForm form={form} onSearch={onSearch} isCancelBtn={isCancelBtn} />
                    </div>
                ) :
                (
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSearch)}>
                            <div className='flex justify-between gap-2.5 relative'>
                                <FormField
                                    control={form.control}
                                    name='orderCode'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 relative'>
                                            <FormControl>
                                                <Input
                                                    className='h-10 pl-10'
                                                    type='text'
                                                    placeholder={t('field.reservation.reservation_code')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <button className='-translate-y-1/2 absolute left-3 size-5 top-1/2'>
                                                <Search />
                                            </button>
                                        </FormItem>
                                    )}
                                />
                                <div>
                                    <Button onClick={() => handleOpenFilter()} type='button' variant='outline' size='icon' className='size-10'>
                                        <ListFilter className='size-5' />
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </Form>
                )}
            <div className='flex justify-end w-full gap-4 my-4 xsm:hidden'>
                {renderButtonTable()}
            </div>
            <div className='layout-card xsm:mt-4 md:mt-6'>
                <Table
                    columns={columns}
                    data={reservationCMSList}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    enableRowSelection={({ original }) => {
                        const accpetCancel: OrderStatusType[] = [ORDER_STATUS.Not_Paid, ORDER_STATUS.Paid, ORDER_STATUS.Add_Not_Paid];
                        return accpetCancel.includes(original.orderStatus);
                    }}
                    onRowSelectionChange={setRowSelected}
                    getRowId={row => row.id?.toString()}
                    enableScrollResTable={isOwner}
                    renderActions={(
                        <div className='xsm:block hidden'>{renderButtonTable()}</div>
                    )}
                />
            </div>
        </>
    );
});
